import { ImageWithFallback } from "@/components/ImageWithFallback";
import { trackViewPlayerContent } from "@/events/sbcMixpanel";
import { generateNavigationUrl } from "@/redux/players/playersSlice";
import { SbcSet, SetVote } from "@/redux/sbcs/sbcSetsSlice";
import { Platform } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import React from "react";
import { useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { SetActionsMenu, SetToggleType } from "../SetActionsMenu";
import { SetProgress } from "../SetProgress";
import { VoteChip } from "../VoteChip";
import { SetHeaderDetails } from "./SetHeaderDetails";
import { SetHeaderSubtitle } from "./SetHeaderSubtitle";
import { SetHeaderTitle } from "./SetHeaderTitle";
import { SetRewardHeader } from "./SetRewardHeader";

type Props = {
  sbcSet: SbcSet;
  platform?: Platform;
  onClickSbcToggle: (toggleType: SetToggleType) => void;
  onVote?: (vote: SetVote) => void;
  isOnSbcPage?: boolean;
};

export const SetHeader = ({
  sbcSet,
  platform,
  onClickSbcToggle,
  onVote,
  isOnSbcPage,
}: Props) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const navigate = useNavigate();
  const { solvedCount, sbcsCount, isFavorite, isHidden, reward } = sbcSet;

  const onClickPlayerRewards = () => {
    if (sbcSet.reward?.type !== "player") {
      return;
    }
    trackViewPlayerContent("Set View");
    const navLink = generateNavigationUrl({
      resourceId: sbcSet.reward?.resourceId,
      name: sbcSet.name,
      basePath: "/players",
    });
    navigate(navLink);
  };

  const isCompleted = solvedCount > 0 && sbcsCount === solvedCount;

  const commonProps = {
    sbcSet,
    platform,
    isCompleted,
    isFavorite,
    isHidden,
    solvedCount,
    sbcsCount,
    reward,
    onClickSbcToggle,
    onVote,
    isOnSbcPage,
    onClickPlayerRewards,
  };

  return isMobile ? (
    <SetHeaderMobile {...commonProps} />
  ) : (
    <SetHeaderDesktop {...commonProps} isOnSbcPage={isOnSbcPage} />
  );
};

type CommonProps = {
  sbcSet: SbcSet;
  platform?: Platform;
  isCompleted: boolean;
  isFavorite: boolean;
  isHidden: boolean;
  solvedCount: number;
  sbcsCount: number;
  reward?: SbcSet["reward"];
  onClickSbcToggle: (toggleType: SetToggleType) => void;
  onVote?: (vote: SetVote) => void;
  isOnSbcPage?: boolean;
  onClickPlayerRewards: () => void;
};

const SetHeaderMobile = ({
  sbcSet,
  platform,
  solvedCount,
  sbcsCount,
  onVote,
  onClickPlayerRewards,
}: CommonProps) => {
  const { t } = useTranslation("sbcs");

  return (
    <div className="flex flex-col gap-y-3 py-2">
      {/* Identity and what it costs, with the vote in the top corner. */}
      <div className="flex justify-between gap-x-3">
        <div className="flex flex-col gap-y-2 flex-1">
          <div className="flex flex-row">
            <ImageWithFallback
              src={sbcSet.imageURL}
              alt={t("alt.logo")}
              className="-m-1 size-12 shrink-0 object-contain"
              loading="lazy"
              fallback={
                import.meta.env.VITE_APP_ASSETS_BASE_URL +
                "/misc/default-sbc.png"
              }
            />
            <div className="flex min-w-0 flex-1 flex-col gap-y-1">
              <SetHeaderTitle
                title={sbcSet.name}
                isNew={sbcSet.isNew}
                hasRefreshed={sbcSet.hasRefreshed}
              />
              <SetHeaderSubtitle {...sbcSet} platform={platform} />
            </div>
          </div>
          <div className="flex flex-col gap-y-2">
            <SetProgress solvedCount={solvedCount} sbcsCount={sbcsCount} />
            <SetHeaderDetails {...sbcSet} />
          </div>
        </div>
        {onVote && (
          <VoteChip
            upVotes={sbcSet.upVotes}
            downVotes={sbcSet.downVotes}
            wasUpvoted={sbcSet.wasUpvoted}
            wasDownvoted={sbcSet.wasDownvoted}
            onUpvote={() => onVote("UPVOTE")}
            onDownvote={() => onVote("DOWNVOTE")}
          />
        )}
      </div>

      {/* Progress and timings run the full width, not indented past the crest. */}

      {sbcSet.reward ? (
        <SetRewardHeader
          reward={sbcSet.reward}
          setName={sbcSet.name}
          setDescription={sbcSet.description}
          compact
          onClick={onClickPlayerRewards}
        />
      ) : (
        <p className="text-xs text-foreground-body">{t("set.noReward")}</p>
      )}
    </div>
  );
};

const SetHeaderDesktop = ({
  sbcSet,
  platform,
  isCompleted,
  isFavorite,
  isHidden,
  solvedCount,
  sbcsCount,
  reward,
  onClickSbcToggle,
  onVote,
  onClickPlayerRewards,
}: CommonProps) => {
  const { t } = useTranslation("sbcs");

  return (
    <div className="flex flex-wrap lg:flex-nowrap mx-auto justify-center gap-16 w-full">
      <div className="flex flex-row flex-1 items-center gap-6">
        <ImageWithFallback
          src={sbcSet.imageURL}
          alt={t("alt.logo")}
          className="object-cover w-24 touch-device:w-32"
          fallback={
            import.meta.env.VITE_APP_ASSETS_BASE_URL + "/misc/default-sbc.png"
          }
        />
        <div className="flex flex-col my-auto flex-1 w-full -ml-6">
          <div className="flex items-center gap-x-3">
            <SetHeaderTitle
              title={sbcSet.name}
              isNew={sbcSet.isNew}
              hasRefreshed={sbcSet.hasRefreshed}
            />
            <SetActionsMenu
              isFavorite={isFavorite}
              isHidden={isHidden}
              isCompleted={isCompleted}
              disableComplete={sbcSet.repeatabilityMode === "UNLIMITED"}
              onMarkAsFavorite={() => onClickSbcToggle("favorite")}
              onHide={() => onClickSbcToggle("hidden")}
              onComplete={() => onClickSbcToggle("completed")}
            />
          </div>
          <div className="flex justify-between pt-2 text-white gap-x-6">
            <div className="flex flex-col w-full gap-y-3">
              <SetHeaderSubtitle {...sbcSet} platform={platform} />
              <SetProgress solvedCount={solvedCount} sbcsCount={sbcsCount} />
              <SetHeaderDetails {...sbcSet} />
            </div>
          </div>
        </div>
      </div>

      <div className="my-4 flex min-w-50 flex-1 items-center gap-x-4 rounded-md">
        {reward ? (
          <SetRewardHeader
            reward={reward}
            setName={sbcSet.name}
            setDescription={sbcSet.description}
            onClick={onClickPlayerRewards}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center min-h-28">
            <p className="text-base text-foreground-body">
              {t("set.noReward")}
            </p>
          </div>
        )}
        <div className="ml-auto flex shrink-0 flex-col items-end gap-y-2">
          {onVote && (
            <VoteChip
              upVotes={sbcSet.upVotes}
              downVotes={sbcSet.downVotes}
              wasUpvoted={sbcSet.wasUpvoted}
              wasDownvoted={sbcSet.wasDownvoted}
              onUpvote={() => onVote("UPVOTE")}
              onDownvote={() => onVote("DOWNVOTE")}
            />
          )}
        </div>
      </div>
    </div>
  );
};
